import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Reply, RefreshCw, Scale } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openNote = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      load();
    }
    setOpen(false);
    navigate(`/supervisor/${n.supervisor_id}`);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="nav-notifications-btn" className="relative text-slate-500 hover:text-emerald-700 h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors">
          <Bell size={18} />
          {data.unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-display text-lg" style={{ fontWeight: 700 }}>Notifications</span>
          {data.unread > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAll} className="text-xs text-emerald-700 hover:underline flex items-center gap-1">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notifications-empty">
              No notifications yet.
            </div>
          ) : (
            data.notifications.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-${n.id}`}
                onClick={() => openNote(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${n.read ? "" : "bg-emerald-50/40"}`}
              >
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${n.type === "checkin" ? "bg-amber-100" : n.type === "legal_hold" ? "bg-rose-100" : n.type === "legal_release" ? "bg-emerald-100" : "bg-emerald-100"}`}>
                  {n.type === "checkin" ? <RefreshCw size={15} className="text-amber-700" /> : n.type === "legal_hold" ? <Scale size={15} className="text-rose-700" /> : n.type === "legal_release" ? <Scale size={15} className="text-emerald-700" /> : <Reply size={15} className="text-emerald-700" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 leading-snug">
                    {n.type === "checkin"
                      ? <>It's been a while since you reviewed <b>{n.supervisor_name}</b>. Has anything changed?</>
                      : n.type === "legal_hold"
                        ? <>Your review of <b>{n.supervisor_name}</b> is temporarily on hold.</>
                        : n.type === "legal_release"
                          ? <>Your review of <b>{n.supervisor_name}</b> is visible again.</>
                          : <><b>{n.supervisor_name}</b> responded to your review{n.review_title ? ` "${n.review_title}"` : ""}.</>}
                  </p>
                  <p className={`text-xs text-slate-400 mt-0.5 ${n.type?.startsWith("legal") ? "" : "truncate"}`}>{n.type === "checkin" ? "Tap to check in: improved, same, or worse." : n.snippet}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-emerald-500 mt-1 shrink-0" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
